import { Task } from "../types";
import { formatLocalDate } from "./workingHours";

export const formatTaskDuration = (minutes: number | null): string => {
  if (minutes == null || minutes <= 0) return "";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
};

export const parseTaskDuration = (input: string): number | null => {
  const trimmed = input.trim();
  if (!trimmed) return null;

  // Match "1h 30m", "1h30m", "2h", "45m"
  const hm = /^(\d+)\s*h\s*(\d+)\s*m$/i.exec(trimmed);
  if (hm) return parseInt(hm[1]) * 60 + parseInt(hm[2]);

  const hOnly = /^(\d+)\s*h$/i.exec(trimmed);
  if (hOnly) return parseInt(hOnly[1]) * 60;

  const mOnly = /^(\d+)\s*m$/i.exec(trimmed);
  if (mOnly) return parseInt(mOnly[1]);

  // Plain number (treated as minutes)
  const num = /^(\d+)$/.exec(trimmed);
  if (num) return parseInt(num[1]);

  return null;
};

export const getPriorityColor = (priority: number): string => {
  switch (priority) {
    case 1:
      return "border-red-700";
    case 2:
      return "border-orange-500";
    case 3:
      return "border-yellow-400";
    default:
      return "border-warm-border";
  }
};

export const parseDateFromTitle = (
  title: string,
): { title: string; date: string | null; time: string | null } => {
  const now = new Date();
  // Local midnight today — day arithmetic stays on the user's calendar day.
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const lowerTitle = title.toLowerCase();

  const patterns: Record<
    string,
    {
      days?: number;
      dayOfWeek?: number;
      time?: string;
      toWeekend?: boolean;
      remove: RegExp;
    }
  > = {
    today: { days: 0, remove: /\btoday\b/gi },
    tomorrow: { days: 1, remove: /\btomorrow\b/gi },
    tonight: { days: 0, time: "20:00", remove: /\btonight\b/gi },
    monday: { dayOfWeek: 1, remove: /\bmonday\b/gi },
    tuesday: { dayOfWeek: 2, remove: /\btuesday\b/gi },
    wednesday: { dayOfWeek: 3, remove: /\bwednesday\b/gi },
    thursday: { dayOfWeek: 4, remove: /\bthursday\b/gi },
    friday: { dayOfWeek: 5, remove: /\bfriday\b/gi },
    saturday: { dayOfWeek: 6, remove: /\bsaturday\b/gi },
    sunday: { dayOfWeek: 0, remove: /\bsunday\b/gi },
    "next week": { days: 7, remove: /\bnext week\b/gi },
    "this weekend": {
      days: 0,
      toWeekend: true,
      remove: /\bthis weekend\b/gi,
    },
  };

  let foundDate: string | null = null;
  let foundTime: string | null = null;
  let cleanedTitle = title;
  let patternMatched = false;

  for (const [key, pattern] of Object.entries(patterns)) {
    if (lowerTitle.includes(key)) {
      const targetDate = new Date(today);

      if (pattern.days !== undefined) {
        targetDate.setDate(today.getDate() + pattern.days);
      } else if (pattern.dayOfWeek !== undefined) {
        const currentDay = today.getDay();
        let daysUntil = pattern.dayOfWeek - currentDay;
        if (daysUntil <= 0) daysUntil += 7;
        targetDate.setDate(today.getDate() + daysUntil);
      } else if (pattern.toWeekend) {
        const daysUntilSaturday = (6 - today.getDay() + 7) % 7;
        targetDate.setDate(today.getDate() + daysUntilSaturday);
      }

      foundDate = formatLocalDate(targetDate);
      if (pattern.time) foundTime = pattern.time;
      cleanedTitle = cleanedTitle.replace(pattern.remove, "");
      patternMatched = true;
      break;
    }
  }

  const timePatterns = [
    // Colon + meridiem (e.g. "at 2:30pm", "at 7:45 pm") must come before the
    // bare 24h colon pattern below: for the spaced form that pattern would
    // otherwise swallow "at 7:45" as 07:45 and leave a dangling "pm" in the
    // title, and for the joined form ("2:30pm") its trailing \b never matches.
    {
      regex: /\bat (\d{1,2}):(\d{2})\s?pm\b/gi,
      extract: (m: RegExpExecArray) =>
        `${String((parseInt(m[1]) % 12) + 12).padStart(2, "0")}:${m[2]}`,
    },
    {
      regex: /\bat (\d{1,2}):(\d{2})\s?am\b/gi,
      extract: (m: RegExpExecArray) =>
        `${String(parseInt(m[1]) % 12).padStart(2, "0")}:${m[2]}`,
    },
    {
      regex: /\bat (\d{1,2}):(\d{2})\b/gi,
      extract: (m: RegExpExecArray) => `${m[1].padStart(2, "0")}:${m[2]}`,
    },
    {
      regex: /\bat (\d{1,2})pm\b/gi,
      extract: (m: RegExpExecArray) => `${(parseInt(m[1]) % 12) + 12}:00`,
    },
    {
      regex: /\bat (\d{1,2})am\b/gi,
      extract: (m: RegExpExecArray) =>
        `${String(parseInt(m[1]) % 12).padStart(2, "0")}:00`,
    },
    {
      regex: /\bat (\d{1,2})\s?pm\b/gi,
      extract: (m: RegExpExecArray) => `${(parseInt(m[1]) % 12) + 12}:00`,
    },
    {
      regex: /\bat (\d{1,2})\s?am\b/gi,
      extract: (m: RegExpExecArray) =>
        `${String(parseInt(m[1]) % 12).padStart(2, "0")}:00`,
    },
  ];

  for (const { regex, extract } of timePatterns) {
    const match = regex.exec(lowerTitle);
    if (match) {
      foundTime = extract(match);
      cleanedTitle = cleanedTitle.replace(regex, "");
      patternMatched = true;
      break;
    }
  }

  // Only normalize whitespace if a pattern was matched
  if (patternMatched) {
    cleanedTitle = cleanedTitle.replace(/\s+/g, " ").trim();
  }

  return { title: cleanedTitle, date: foundDate, time: foundTime };
};

export function getDaysAgo(createdAt: string): {
  days: number;
  label: string;
  tooltip: string;
} {
  const created = new Date(createdAt);
  const now = new Date();

  // Normalize to midnight for accurate day calculation (timezone-safe)
  const createdDate = new Date(
    created.getFullYear(),
    created.getMonth(),
    created.getDate(),
  );
  const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  const diffMs = nowDate.getTime() - createdDate.getTime();
  const days = Math.round(diffMs / (1000 * 60 * 60 * 24));

  // Always use numeric labels for consistency
  if (days === 0) return { days: 0, label: "0", tooltip: "Created today" };
  if (days === 1) return { days: 1, label: "1", tooltip: "Created yesterday" };
  return { days, label: String(days), tooltip: `Created ${days} days ago` };
}

export const escapeRegex = (str: string): string => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

export const stripHtmlAndImages = (html: string): string => {
  if (!html) return "";

  // Create a temporary div to parse HTML
  const temp = document.createElement("div");
  temp.innerHTML = html;

  // Remove image elements
  const images = temp.querySelectorAll("img");
  images.forEach((img) => img.remove());

  // Get text content
  return temp.textContent || temp.innerText || "";
};

export interface SearchResult {
  task: Task;
  matchType: "title" | "description" | "subtask";
  matchedSubtask?: { id: string; title: string };
}

export const searchTasks = (
  tasks: Task[],
  searchTerm: string,
): SearchResult[] => {
  if (!searchTerm.trim()) return [];

  const term = searchTerm.toLowerCase();
  const results: SearchResult[] = [];

  tasks.forEach((task) => {
    // Search in title
    if (task.title.toLowerCase().includes(term)) {
      results.push({ task, matchType: "title" });
      return;
    }

    // Search in description (stripped HTML)
    const plainDescription = stripHtmlAndImages(task.description);
    if (plainDescription.toLowerCase().includes(term)) {
      results.push({ task, matchType: "description" });
      return;
    }

    // Search in subtasks
    if (task.subtasks && task.subtasks.length > 0) {
      const matchingSubtask = task.subtasks.find((st) =>
        st.title.toLowerCase().includes(term),
      );
      if (matchingSubtask) {
        results.push({
          task,
          matchType: "subtask",
          matchedSubtask: matchingSubtask,
        });
      }
    }
  });

  // Sort: title matches first, then description, then subtasks
  return results.sort((a, b) => {
    const order = { title: 0, description: 1, subtask: 2 };
    return order[a.matchType] - order[b.matchType];
  });
};

// Split a string into grapheme clusters so multi-codepoint emoji (flags, ZWJ
// sequences) stay together. Code-point splitting (`Array.from`) breaks the
// Belgian flag 🇧🇪 into two regional indicators that browsers then render as
// dotted-box letters. `Intl.Segmenter` lives in ES2022; the local type below
// covers the slice we use under this project's ES2020 lib target.
type GraphemeSegmenter = {
  segment(input: string): Iterable<{ segment: string }>;
};
type SegmenterCtor = new (
  locale?: string,
  options?: { granularity: "grapheme" },
) => GraphemeSegmenter;

const graphemeSegmenter: GraphemeSegmenter | null = (() => {
  const intl = Intl as unknown as { Segmenter?: SegmenterCtor };
  return intl.Segmenter
    ? new intl.Segmenter(undefined, { granularity: "grapheme" })
    : null;
})();

export const splitGraphemes = (str: string): string[] =>
  graphemeSegmenter
    ? Array.from(graphemeSegmenter.segment(str), (s) => s.segment)
    : Array.from(str);

/** How many tasks carry a given tag. */
export const countTasksWithTag = (tasks: Task[], tagId: string): number =>
  tasks.filter((task) => task.tags?.some((tag) => tag.id === tagId)).length;
